const ChromaJS = require('chroma-js');
var _ = require('lodash');

// Oklab conversion functions
function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c) {
    return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function rgbToOklab(r, g, b) {
    // Normalize RGB values
    r /= 255; g /= 255; b /= 255;
    
    // Convert to linear RGB
    r = srgbToLinear(r);
    g = srgbToLinear(g);
    b = srgbToLinear(b);
    
    // Convert to LMS
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    
    // Convert to Oklab
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);
    
    return [
        0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
        1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
        0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
    ];
}

function oklabToRgb(L, a, b) {
    // Convert to LMS
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
    
    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;
    
    // Convert to linear RGB
    let r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    let bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
    
    // Convert to sRGB
    r = linearToSrgb(r);
    g = linearToSrgb(g);
    bl = linearToSrgb(bl);
    
    // Clamp and convert to 0-255
    r = Math.max(0, Math.min(1, r)) * 255;
    g = Math.max(0, Math.min(1, g)) * 255;
    bl = Math.max(0, Math.min(1, bl)) * 255;
    
    return [Math.round(r), Math.round(g), Math.round(bl)];
}

function rgbToOklch(r, g, b) {
    const [L, a, bVal] = rgbToOklab(r, g, b);
    const C = Math.sqrt(a * a + bVal * bVal);
    let H = Math.atan2(bVal, a) * 180 / Math.PI;
    if (H < 0) H += 360;
    return [H, C, L]; // Return in HCL order for compatibility
}

function oklchToRgb(H, C, L) {
    const hRad = H * Math.PI / 180;
    const a = C * Math.cos(hRad);
    const b = C * Math.sin(hRad);
    return oklabToRgb(L, a, b);
}

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : null;
}

function rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }).join("");
}

function isValidColor(r, g, b) {
    // Check if RGB values are within valid range and not clipped
    return r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255;
}

module.exports = function submitHexInOklab(seedColor) {
    const submittedHex = seedColor;

    if (submittedHex) {
        const [r, g, b] = hexToRgb(submittedHex);
        if (!r && r !== 0) return "Invalid hex color";
        
        const newOklch = rgbToOklch(r, g, b);
        const newHue = isNaN(newOklch[0]) ? 0 : Math.round(newOklch[0] * 10) / 10; // More precision for Oklab
        const newChroma = Math.round(newOklch[1] * 1000) / 1000; // Oklab chroma is much smaller scale
        const newLight = Math.round(newOklch[2] * 1000) / 1000; // Oklab lightness is 0-1 range
        
        const oklchMap = calculateOklchMap({
            startHue: newHue,
            endHue: newHue,
            loopHue: false
        });
        
        const relativeChromacity = calculateRelativeChromacity(oklchMap, newChroma, newLight);
        const finalColorPalette = calculatePalette({ oklchMap, relativeChromacity });
        return finalColorPalette;
    } else {
        return "Something went wrong in generating colors";
    }
}

function calculateOklchMap(props) {
    const { startHue, endHue, loopHue } = props;
    const scaleLight = 101; // 0-100 mapped to 0-1 in Oklab
    const scaleChroma = 80;  // Reduced scale as Oklab chroma is typically 0-0.4
    let oklchMap = [];
    
    // First matrix is lightness (0-1 in Oklab, mapped from 0-100)
    _.times(scaleLight, (lightIndex) => {
        let hue, hueDiff;
        let rowColors = [];
        const lightness = lightIndex / 100; // Convert to 0-1 range for Oklab

        if (loopHue) {
            hueDiff = startHue > endHue ? 360 - startHue + endHue : 360 - endHue + startHue;
            hue = endHue + (hueDiff / 100) * lightIndex;
            if (hue > 360) hue = hue - 360;
        } else {
            hueDiff = endHue - startHue;
            hue = endHue - (hueDiff / 100) * lightIndex;
        }
        
        // Second matrix is chroma (0-0.4 typical range in Oklab)
        _.times(scaleChroma, (chromaIndex) => {
            const chroma = (chromaIndex / scaleChroma) * 0.4; // Scale to realistic Oklab chroma range
            
            try {
                const [r, g, bl] = oklchToRgb(hue, chroma, lightness);
                
                // Only add valid colors (not clipped)
                if (isValidColor(r, g, bl)) {
                    const colorHex = rgbToHex(r, g, bl);
                    const contrastRatio = parseFloat(
                        ChromaJS.contrast('#fff', colorHex).toFixed(2)
                    );

                    rowColors.push({
                        hue: Math.round(hue * 10) / 10,
                        light: Math.round(lightness * 1000) / 1000,
                        chroma: Math.round(chroma * 1000) / 1000,
                        hex: colorHex,
                        contrastRatio,
                        contrastAALarge: contrastRatio >= 3,
                        contrastAA: contrastRatio >= 4.5
                    });
                }
            } catch (error) {
                // Skip invalid color combinations
                return; // Skip this iteration instead of using continue
            }
        });
        
        oklchMap.push(rowColors);
    });

    return oklchMap;
}

function calculatePalette(props) {
    const { relativeChromacity, oklchMap } = props;
    // Adjusted palette lights for Oklab (0-1 range)
    const paletteLights = [0.96, 0.91, 0.81, 0.71, 0.66, 0.61, 0.49, 0.39, 0.29, 0.19, 0.14, 0.08];
    const paletteLabels = [
        '95', '90', '80', '70', '65', '60', '50', '40', '30', '20', '15', '10'
    ];
    let palette = [];

    paletteLights.forEach((targetLight, i) => {
        // Find the closest lightness level in our map
        const lightIndex = Math.round(targetLight * 100);
        const colorRow = oklchMap[Math.min(lightIndex, oklchMap.length - 1)];
        
        if (colorRow && colorRow.length > 0) {
            let colorChroma = Math.max(
                Math.floor(colorRow.length * (relativeChromacity / 100) - 1),
                0
            );

            // Chroma max locks for certain lightness levels (adapted for Oklab)
            if (targetLight >= 0.95) {
                colorChroma = Math.min(colorChroma, Math.floor(colorRow.length * 0.15)); // ~15% of available chroma
            } else if (targetLight >= 0.90) {
                colorChroma = Math.min(colorChroma, Math.floor(colorRow.length * 0.30)); // ~30% of available chroma
            } else if (targetLight >= 0.80) {
                colorChroma = Math.min(colorChroma, Math.floor(colorRow.length * 0.60)); // ~60% of available chroma
            }

            colorChroma = Math.min(colorChroma, colorRow.length - 1);
            
            let colorDetail = { ...colorRow[colorChroma] };
            colorDetail.label = paletteLabels[i];
            
            palette.push(colorDetail);
        }
    });

    return palette;
}

function calculateRelativeChromacity(oklchMap, newChroma, newLight) {
    // Find the closest lightness level in our map
    const lightIndex = Math.round(newLight * 100);
    const colorRow = oklchMap[Math.min(lightIndex, oklchMap.length - 1)];

    if (!colorRow || colorRow.length === 0) {
        return 0;
    }

    // Get max chroma for palette
    let maxChroma = 0;
    oklchMap.forEach((lightRow) => {
        const rowChromacity = lightRow.length;
        if (rowChromacity > maxChroma) maxChroma = rowChromacity;
    });

    // Convert chroma to index in the row (since we're using indices)
    const chromaIndex = Math.round((newChroma / 0.4) * colorRow.length);
    const boundedChromaIndex = Math.min(chromaIndex, colorRow.length - 1);

    // Calculate relative chromacity
    const rowChromaPct = boundedChromaIndex / colorRow.length;
    const paletteChromaPct = boundedChromaIndex / maxChroma;
    const relativeChromacity = Math.min(
        Math.round(((rowChromaPct + paletteChromaPct) / 2) * 100),
        100
    );

    return relativeChromacity;
}